/**
 * Application bootstrap coordination.
 *
 * Bridges the initial splash screen (managed in `main.ts`, outside Angular) with
 * the moment the boot flow concludes and the first meaningful screen is ready to
 * show (login form or auto-login target view).
 *
 * It is a module-level singleton (not an Angular service) on purpose: the splash
 * must be cleared even before/regardless of Angular dependency injection, so both
 * the bootstrap entry point and the application code share the same instance.
 */

let _resolveReady: (() => void) | null = null;
let _isReady = false;

const _ready: Promise<void> = new Promise<void>((resolve) => {
    _resolveReady = resolve;
});

export const appBootstrap = {
    /**
     * Resolves once the initial boot/login flow has concluded and the first screen
     * is ready to be shown. The splash screen is kept visible until this resolves.
     */
    get OnReady(): Promise<void> {
        return _ready;
    },

    /** True once {@link appBootstrap.MarkReady} has been called. */
    get IsReady(): boolean {
        return _isReady;
    },

    /**
     * Signals that the initial boot/login flow has concluded and the first screen
     * is ready to show. Safe to call multiple times; only the first call has effect.
     */
    MarkReady(): void {
        if (_resolveReady){
            const _resolve = _resolveReady;
            _resolveReady = null;
            _isReady = true;
            _resolve();
        }
    }
};
